'use client'

import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components/Button'

import { cva, cx } from '../../../styled-system/css'

const colors = ['primary', 'secondary', 'success', 'info', 'danger', 'cyan', 'neutral'] as const
type Variant = 'outline' | 'solid' | 'plain'

export const button = cva({
    base: {
        width: 'full',
        borderRadius: '[var(--Button-radius)]',
        borderWidth: 'sm',
        borderStyle: 'solid', // reset
        outline: 'none',
        px: '3',
        py: '0',
        transition: 'colors',
        transitionDuration: 'sm',
        lineHeight: 'normal',
        _disabled: {
            pointerEvents: 'none',
        },
    },

    variants: {
        color: buildObject(colors, (color) => ({
            _focus: {
                boxShadow: '[0 0 0 0.2rem var(--shadow-color)]',
                boxShadowColor: `[${color}/25]`,
            },
        })),

        size: {
            sm: { fontSize: 'sm', borderRadius: 'sm' },
            md: { fontSize: 'md', paddingY: '1.5', borderRadius: 'md' },
            lg: { fontSize: 'xl', paddingY: '2', borderRadius: 'lg' },
        },

        variant: {
            solid: {
                fontFamily: 'montserrat',
                _disabled: {
                    backgroundColor: 'gray.400',
                    borderColor: 'gray.400',
                },
            },
            outline: {
                fontFamily: 'montserrat',
                _disabled: {
                    filter: '[brightness(1.15)]',
                },
            },
            plain: {
                backgroundColor: 'transparent',
                borderColor: 'transparent',
                _hover: {
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                },
                _pressed: {
                    backgroundColor: 'transparent',
                    borderColor: 'transparent',
                },
                _disabled: {
                    filter: '[brightness(1.15)]',
                },
            },
        },
    },

    defaultVariants: {
        variant: 'solid',
        color: 'primary',
        size: 'md',
    },

    compoundVariants: colors.flatMap((color) => [
        {
            color,
            variant: ['solid', 'outline'] as Variant[],
            css: {
                color: 'white',
                backgroundColor: `${color}.500`,
                borderColor: `${color}.500`,
                _hover: {
                    backgroundColor: `${color}.600`,
                    borderColor: `${color}.600`,
                },
                _pressed: {
                    backgroundColor: `[${color}.700!]`,
                    borderColor: `[${color}.700!]`,
                },
            },
        },
        {
            color,
            variant: ['outline'] as Variant[],
            css: {
                color,
                backgroundColor: 'transparent',
                _pressed: { color: 'white' },
                _hover: { color: 'white' },
            },
        },
        {
            color,
            variant: ['plain'] as Variant[],
            css: {
                width: 'auto',
                borderWidth: '[0]',
                paddingY: '0',
                paddingX: '0',
                color,
                _hover: {
                    color: `${color}.600`,
                },
                _pressed: {
                    color: `${color}.800`,
                },
            },
        },
    ]),
})

interface ButtonProps extends AriaButtonProps {
    className?: string
    color?: (typeof colors)[number]
    size?: 'sm' | 'md' | 'lg'
    variant?: 'solid' | 'outline' | 'plain'
}

export function Button(props: ButtonProps) {
    const [buttonProps, { className, children, ...otherProps }] = button.splitVariantProps(props)
    const classes = button(buttonProps)

    return (
        <AriaButton className={cx(classes, className)} {...otherProps}>
            {children}
        </AriaButton>
    )
}

function buildObject<T extends string, U>(arr: readonly T[], mapFn: (x: T) => U): Record<T, U> {
    return Object.fromEntries(arr.map((x) => [x, mapFn(x)])) as Record<T, U>
}
